import React, { useLayoutEffect, useState } from "react";
import { IOption, useDropdown } from "../../hooks/UseDropdown";

interface ICRMDropdownComponent<A extends string> {
    FaceComponent: (props: TDropdownFaceProps<A>) => JSX.Element; 
    OptionsComponent: (props: TDropdownOptionsProps<A>) => JSX.Element;

    options: Array<IOption<A>>;
    
    value: A | Array<A>;
    valuePrefix?: string;
    displayError?: boolean;
    disabled?: boolean;
    placeholder?: string;
};

export type TDropdownFaceProps<A extends string> = {
    isOpen: boolean;
    faceText: JSX.Element;
    toggleOpen: () => void;

    value: A | Array<A>;
    valuePrefix?: string;
    displayError?: boolean;
    disabled?: boolean;
    placeholder?: string;
};

export type TDropdownOptionsProps<A extends string> = {
    options: Array<IOption<A>>;
    isOptionSelected: (value: A) => boolean;
    toggleSelectedValue: (value: A) => A[];
    onCloseDropdown: () => void;
}

const DESIRED_MAX_HEIGHT = 400;
const VIEWPORT_MARGIN = 8;
const MENU_MAX_HEIGHT_VAR = "--crm-dropdown-menu-max-height";

export const CRMDropdownBase = <A extends string>(props: ICRMDropdownComponent<A>): JSX.Element => {

    const {
        ref,
        isOpen,
        setIsOpen,
        isOptionSelected,
        toggleSelectedValue,
        getFaceText,
     } = useDropdown<A, IOption<A>>(props.value, props.options);

    const [openUp, setOpenUp] = useState<boolean>(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (el === null) {
            return;
        }

        if (!isOpen) {
            setOpenUp(false);
            el.style.removeProperty(MENU_MAX_HEIGHT_VAR);
            return;
        }

        const rect = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const shouldOpenUp = spaceBelow < DESIRED_MAX_HEIGHT && spaceAbove > spaceBelow;
        const available = (shouldOpenUp ? spaceAbove : spaceBelow) - VIEWPORT_MARGIN;
        const clampedMaxHeight = Math.max(0, Math.min(DESIRED_MAX_HEIGHT, available));

        setOpenUp(shouldOpenUp);
        el.style.setProperty(MENU_MAX_HEIGHT_VAR, `${clampedMaxHeight}px`);
    }, [isOpen, ref]);

    return (
        <div
            ref={ref}
            className={openUp ? "crm-dropdown-base crm-dropdown-base--flip-up" : "crm-dropdown-base"}
        >
            <props.FaceComponent
                isOpen={isOpen}
                toggleOpen={() => setIsOpen(!isOpen)}
                faceText={getFaceText()}
                {...props}
            />

            {/* OPTIONS */}
            {isOpen &&
                <props.OptionsComponent
                    isOptionSelected={isOptionSelected}
                    onCloseDropdown={() => setIsOpen(false)}
                    toggleSelectedValue={toggleSelectedValue}
                    {...props}
                />
            }
        </div>
    );
}
